import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { day, money } from '../../lib/format.ts';
import { useAction, useSave } from './common.tsx';
import { costText, parseCost, parseQty, unitCost, unitLabel, withUnit, type Overview, type Unit } from './Stock.tsx';

/**
 * Supplier price lists and calls for quotes (modules/purchase rfq.ts): what each supplier
 * charges, and asking several suppliers at once, comparing their answers and awarding.
 */

interface PriceRow {
  id: string;
  supplier: string;
  supplier_name: string;
  item: string;
  item_name: string;
  unit: Unit;
  price: number;
  min_qty: number;
  lead_days: number | null;
  valid_from: string | null;
  valid_to: string | null;
  supplier_code: string | null;
  current: boolean;
}
interface SupplierLite {
  id: string;
  name: string;
  active: boolean;
}

/* ---------------------------------------------------------------- Price list of one supplier */

export function SupplierPrices({ supplier, manage }: { supplier: string; manage: boolean }) {
  const { data, reload } = useAction<{ today: string; rows: PriceRow[] }>('purchase.supplier_prices', { supplier });
  const [editing, setEditing] = useState<PriceRow | 'new' | null>(null);
  return (
    <section className="sp-panel" aria-labelledby="sp-title">
      <div className="sp-head">
        <div>
          <h3 id="sp-title">{t('sp.title')}</h3>
          <p className="muted small">{t('sp.hint')}</p>
        </div>
        {manage ? (
          <Button size="sm" icon="plus" onClick={() => setEditing('new')}>
            {t('sp.add')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={60} />
      ) : !data.rows.length ? (
        <p className="muted small">{t('sp.empty')}</p>
      ) : (
        <ul className="sp-list">
          {data.rows.map((p) => (
            <li key={p.id}>
              <button className="sp-row" onClick={() => manage && setEditing(p)} disabled={!manage}>
                <span className="list-main">
                  <span className="list-title">
                    {p.item_name}
                    {p.supplier_code ? <span className="muted small"> · {p.supplier_code}</span> : null}
                  </span>
                  <span className="list-sub">
                    {[
                      p.min_qty ? t('sp.from_qty', { qty: withUnit(p.min_qty, p.unit) }) : t('sp.any_qty'),
                      p.lead_days !== null ? tn('sp.lead', p.lead_days) : null,
                      p.valid_to ? t('sp.until', { date: day(p.valid_to) }) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <strong className="tabular">{unitCost(p.price, p.unit)}</strong>
                {!p.current ? <StatusBadge tone="neutral" icon="clock" label={t('sp.not_now')} /> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      {editing ? <PriceDialog supplier={supplier} price={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onDone={() => (setEditing(null), void reload())} /> : null}
    </section>
  );
}

function PriceDialog({ supplier, price, onClose, onDone }: { supplier: string; price: PriceRow | null; onClose: () => void; onDone: () => void }) {
  const { busy, run } = useSave();
  const { data: stock } = useAction<Overview>('inventory.overview');
  const [item, setItem] = useState(price?.item ?? '');
  const [cost, setCost] = useState(price ? costText(price.price) : '');
  const [minQty, setMinQty] = useState(price?.min_qty ? String(price.min_qty) : '');
  const [lead, setLead] = useState(price?.lead_days !== null && price?.lead_days !== undefined ? String(price.lead_days) : '');
  const [from, setFrom] = useState(price?.valid_from ?? '');
  const [to, setTo] = useState(price?.valid_to ?? '');
  const [code, setCode] = useState(price?.supplier_code ?? '');
  const it = stock?.items.find((i) => i.id === item);
  const c = parseCost(cost);
  const q = minQty.trim() ? parseQty(minQty) : 0;
  const l = lead.trim() ? (/^\d{1,3}$/.test(lead.trim()) ? Number(lead) : NaN) : null;
  const ok = item && c !== null && q !== null && !Number.isNaN(l) && (!from || !to || to >= from);
  const save = async () => {
    const done = await run(
      () =>
        action('purchase.save_supplier_price', {
          price_id: price?.id,
          supplier,
          item,
          price: c,
          min_qty: q,
          lead_days: l,
          valid_from: from || null,
          valid_to: to || null,
          supplier_code: code.trim() || null,
        }),
      t('sp.saved'),
    );
    if (done) onDone();
  };
  const remove = async () => {
    if (price && (await run(() => action('purchase.delete_supplier_price', { price_id: price.id }), t('sp.removed')))) onDone();
  };
  return (
    <Dialog
      title={price ? price.item_name : t('sp.add')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {price ? (
            <Button variant="ghost" icon="trash" onClick={() => void remove()} disabled={busy}>
              {t('sp.remove')}
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          )}
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {!stock ? (
        <Skeleton h={200} />
      ) : (
        <div className="form-stack">
          <Field label={t('sp.item')}>
            <select className="input" value={item} disabled={!!price} onChange={(e) => setItem(e.target.value)} data-autofocus>
              <option value="">{t('sp.pick_item')}</option>
              {stock.items
                .filter((i) => i.active || i.id === item)
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
            </select>
          </Field>
          <div className="form-grid">
            <Field label={it ? t('sp.price_per', { unit: unitLabel(it.unit) }) : t('sp.price')} error={cost && c === null ? t('sp.price_rule') : null}>
              <input className="input tabular" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />
            </Field>
            <Field label={t('sp.min_qty')} hint={t('sp.min_qty_hint')} error={q === null ? t('sp.qty_rule') : null}>
              <input className="input tabular" inputMode="decimal" value={minQty} onChange={(e) => setMinQty(e.target.value)} placeholder="0" />
            </Field>
            <Field label={t('sp.lead_days')} error={Number.isNaN(l) ? t('sp.lead_rule') : null}>
              <input className="input" inputMode="numeric" maxLength={3} value={lead} onChange={(e) => setLead(e.target.value)} />
            </Field>
            <Field label={t('sp.code')}>
              <input className="input" maxLength={40} value={code} onChange={(e) => setCode(e.target.value)} />
            </Field>
            <Field label={t('sp.valid_from')}>
              <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label={t('sp.valid_to')} error={from && to && to < from ? t('sp.dates_rule') : null}>
              <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Calls for quotes */

type RfqState = 'draft' | 'sent' | 'awarded' | 'cancelled';
interface RfqRow {
  id: string;
  name: string;
  number: number;
  state: RfqState;
  deadline: string | null;
  created_at: string;
  items: number;
  asked: number;
  replied: number;
  suppliers: string | null;
}
interface RfqDetail {
  rfq: { id: string; name: string; state: RfqState; deadline: string | null; note: string | null; location: string | null; lines: { item: string; qty: number }[]; cancel_reason: string | null };
  suppliers: {
    id: string;
    supplier: string;
    name: string;
    email: string | null;
    phone: string | null;
    state: 'invited' | 'replied' | 'declined';
    prices: Record<string, number>;
    lead_days: number | null;
    note: string | null;
    order: string | null;
    total: number;
    offered: number;
    complete: boolean;
  }[];
  lines: { item: string; name: string; unit: Unit; qty: number; best: string | null; best_price: number | null }[];
  cheapest: string | null;
  best_mix: number;
  all_offered: boolean;
}
const RFQ_TONE: Record<RfqState, Tone> = { draft: 'neutral', sent: 'info', awarded: 'ok', cancelled: 'neutral' };
const RFQ_ICON = { draft: 'edit', sent: 'send', awarded: 'check', cancelled: 'x' } as const;

export function RfqTab({ manage, onOrders, creating, onCreated }: { manage: boolean; onOrders: () => Promise<void>; creating: boolean; onCreated: () => void }) {
  const { data, reload } = useAction<RfqRow[]>('purchase.rfqs');
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <section className="list-card" aria-label={t('rfq.title')}>
        {!data ? (
          <Skeleton h={160} />
        ) : !data.length ? (
          <EmptyState icon="messages" title={t('rfq.empty')} body={manage ? t('rfq.empty_body') : undefined} />
        ) : (
          data.map((r) => (
            <button key={r.id} className="list-row stock-row" onClick={() => setOpen(r.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name="messages" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">
                  {r.name} · {tn('rfq.items', r.items)}
                </span>
                <span className="list-sub">
                  {[r.suppliers, r.deadline ? t('rfq.by', { date: day(r.deadline) }) : null, r.state === 'sent' ? t('rfq.replied_of', { n: r.replied, of: r.asked }) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <StatusBadge tone={RFQ_TONE[r.state]} icon={RFQ_ICON[r.state]} label={t(`rfq.s_${r.state}` as MessageKey)} />
            </button>
          ))
        )}
      </section>
      {creating ? <RfqEditor rfq={null} onClose={onCreated} onSaved={(id) => (onCreated(), void reload(), setOpen(id))} /> : null}
      {open ? <RfqDialog id={open} manage={manage} onClose={() => setOpen(null)} onChanged={() => void reload()} onOrders={onOrders} /> : null}
    </>
  );
}

function RfqEditor({ rfq, onClose, onSaved }: { rfq: RfqDetail | null; onClose: () => void; onSaved: (id: string) => void }) {
  const { busy, run } = useSave();
  const { data: stock } = useAction<Overview>('inventory.overview');
  const { data: suppliers } = useAction<SupplierLite[]>('purchase.suppliers');
  const [lines, setLines] = useState<{ item: string; qty: string }[]>(rfq?.rfq.lines.map((l) => ({ item: l.item, qty: String(l.qty) })) ?? [{ item: '', qty: '' }]);
  const [asked, setAsked] = useState<Set<string>>(new Set(rfq?.suppliers.map((s) => s.supplier) ?? []));
  const [deadline, setDeadline] = useState(rfq?.rfq.deadline ?? '');
  const [note, setNote] = useState(rfq?.rfq.note ?? '');
  const byId = new Map((stock?.items ?? []).map((i) => [i.id, i]));
  const filled = lines.filter((l) => l.item || l.qty);
  const bad = filled.some((l) => !l.item || !(parseQty(l.qty)! > 0));
  const twice = new Set(filled.map((l) => l.item)).size !== filled.length;
  const ok = filled.length > 0 && !bad && !twice && asked.size >= 2;
  const set = (i: number, k: 'item' | 'qty', v: string) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const save = (send: boolean) =>
    void run(
      async () => {
        const r = await action<{ id: string }>('purchase.save_rfq', {
          rfq: rfq?.rfq.id,
          deadline: deadline || null,
          note: note.trim() || null,
          lines: filled.map((l) => ({ item: l.item, qty: parseQty(l.qty) })),
          suppliers: [...asked],
        });
        if (send) await action('purchase.send_rfq', { rfq: r.id });
        onSaved(r.id);
      },
      send ? t('rfq.sent_ok') : t('rfq.saved'),
    );
  return (
    <Dialog
      title={rfq ? rfq.rfq.name : t('rfq.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="muted">{asked.size < 2 ? t('rfq.two_rule') : tn('rfq.asking', asked.size)}</span>
          <span className="foot-actions">
            <Button icon="edit" onClick={() => save(false)} busy={busy} disabled={!ok}>
              {t('buy.save_draft')}
            </Button>
            <Button variant="primary" icon="send" onClick={() => save(true)} busy={busy} disabled={!ok}>
              {t('rfq.send')}
            </Button>
          </span>
        </>
      }
    >
      {!stock || !suppliers ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <p className="muted">{t('rfq.explain')}</p>
          <h3 className="field-label">{t('rfq.what')}</h3>
          {lines.map((l, i) => {
            const it = byId.get(l.item);
            return (
              <div key={i} className="rfq-line">
                <select className="input" aria-label={t('rfq.item_n', { n: i + 1 })} value={l.item} onChange={(e) => set(i, 'item', e.target.value)}>
                  <option value="">{t('sp.pick_item')}</option>
                  {stock.items
                    .filter((x) => x.active || x.id === l.item)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
                <span className="input-unit">
                  <input className="input tabular" inputMode="decimal" aria-label={t('rfq.qty_n', { n: i + 1 })} value={l.qty} onChange={(e) => set(i, 'qty', e.target.value)} />
                  <span>{it ? unitLabel(it.unit) : ''}</span>
                </span>
                <IconButton icon="trash" label={t('rfq.remove_line', { n: i + 1 })} onClick={() => setLines(lines.length > 1 ? lines.filter((_, j) => j !== i) : [{ item: '', qty: '' }])} />
              </div>
            );
          })}
          {twice ? (
            <p className="field-msg" role="alert">
              <Icon name="alert" size={14} /> {t('rfq.twice')}
            </p>
          ) : null}
          <div className="row-actions">
            <Button size="sm" icon="plus" onClick={() => setLines([...lines, { item: '', qty: '' }])} disabled={lines.length >= 100}>
              {t('rfq.add_line')}
            </Button>
          </div>
          <fieldset className="option-group">
            <legend className="field-label">{t('rfq.who')}</legend>
            <div className="rfq-suppliers">
              {suppliers
                .filter((s) => s.active || asked.has(s.id))
                .map((s) => (
                  <label key={s.id} className="check-row">
                    <input
                      type="checkbox"
                      checked={asked.has(s.id)}
                      onChange={(e) => {
                        const next = new Set(asked);
                        if (e.target.checked) next.add(s.id);
                        else next.delete(s.id);
                        setAsked(next);
                      }}
                    />
                    <span>{s.name}</span>
                  </label>
                ))}
            </div>
          </fieldset>
          <div className="form-grid">
            <Field label={t('rfq.deadline')} hint={t('rfq.deadline_hint')}>
              <input className="input" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </Field>
            <Field label={t('buy.note')}>
              <input className="input" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function RfqDialog({ id, manage, onClose, onChanged, onOrders }: { id: string; manage: boolean; onClose: () => void; onChanged: () => void; onOrders: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [v, setV] = useState(0);
  const { data: d } = useAction<RfqDetail>('purchase.rfq', { rfq: id, v });
  const [editing, setEditing] = useState(false);
  const [answer, setAnswer] = useState<RfqDetail['suppliers'][number] | null>(null);
  const [made, setMade] = useState<{ supplier: string; number: number }[] | null>(null);
  const refresh = () => {
    setV((x) => x + 1);
    onChanged();
  };
  if (editing && d) return <RfqEditor rfq={d} onClose={() => setEditing(false)} onSaved={() => (setEditing(false), refresh())} />;
  const r = d?.rfq;
  const award = (mode: 'best' | 'supplier', supplier?: string) =>
    void run(async () => {
      const res = await action<{ orders: { supplier: string; number: number }[] }>('purchase.award_rfq', { rfq: id, mode, supplier, remember: true });
      setMade(res.orders);
      refresh();
    }, t('rfq.awarded_ok'));
  const send = () => void run(async () => (await action('purchase.send_rfq', { rfq: id }), refresh()), t('rfq.sent_ok'));
  const cancel = () => void run(async () => (await action('purchase.cancel_rfq', { rfq: id, reason: t('rfq.cancel_reason') }), refresh()), t('rfq.cancelled_ok'));
  const replied = d?.suppliers.filter((s) => s.state === 'replied') ?? [];
  const nameOf = (s: string) => d?.suppliers.find((x) => x.supplier === s)?.name ?? '';
  return (
    <Dialog
      title={r ? r.name : t('rfq.title')}
      onClose={() => (made ? void onOrders() : undefined, onClose())}
      size="xl"
      footer={
        r && manage && (r.state === 'draft' || r.state === 'sent') ? (
          <span className="foot-actions">
            {r.state === 'draft' || r.state === 'sent' ? (
              <Button variant="ghost" icon="x" onClick={cancel} disabled={busy}>
                {t('rfq.cancel')}
              </Button>
            ) : null}
            {r.state === 'draft' ? (
              <>
                <Button icon="edit" onClick={() => setEditing(true)}>
                  {t('rfq.edit')}
                </Button>
                <Button variant="primary" icon="send" onClick={send} busy={busy}>
                  {t('rfq.send')}
                </Button>
              </>
            ) : null}
            {r.state === 'sent' ? (
              <Button variant="primary" icon="check" onClick={() => award('best')} busy={busy} disabled={!d!.all_offered}>
                {t('rfq.award_best', { total: money(d!.best_mix) })}
              </Button>
            ) : null}
          </span>
        ) : null
      }
    >
      {!d || !r ? (
        <Skeleton h={280} />
      ) : (
        <div className="form-stack">
          <p className="muted">{[t(`rfq.s_${r.state}` as MessageKey), r.deadline ? t('rfq.by', { date: day(r.deadline) }) : null, r.note, r.cancel_reason].filter(Boolean).join(' · ')}</p>
          {made ? (
            <p className="callout callout-ok" role="status">
              <Icon name="checkCircle" size={16} /> {t('rfq.made', { list: made.map((m) => `PO-${m.number} · ${nameOf(m.supplier)}`).join(', ') })}
            </p>
          ) : null}
          {r.state === 'sent' && !d.all_offered && replied.length ? <p className="callout callout-info">{t('rfq.wait_all')}</p> : null}
          <div className="table-wrap">
            <table className="data-table rfq-compare" aria-label={t('rfq.compare')}>
              <thead>
                <tr>
                  <th className="left">{t('sp.item')}</th>
                  <th>{t('rfq.qty')}</th>
                  {d.suppliers.map((s) => (
                    <th key={s.id}>
                      <span className="rfq-sup">
                        {s.name}
                        <span className="muted small">{s.state === 'replied' ? (s.lead_days !== null ? tn('sp.lead', s.lead_days) : t('rfq.replied')) : t(`rfq.a_${s.state}` as MessageKey)}</span>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.lines.map((l) => (
                  <tr key={l.item}>
                    <td className="left">{l.name}</td>
                    <td>{withUnit(l.qty, l.unit)}</td>
                    {d.suppliers.map((s) => {
                      const p = s.prices[l.item];
                      const best = s.state === 'replied' && l.best === s.supplier;
                      return (
                        <td key={s.id} className={best ? 'rfq-best' : p === undefined ? 'muted' : ''}>
                          {p === undefined ? (
                            '–'
                          ) : (
                            <span className="rfq-price">
                              {money(Math.round(p * l.qty))}
                              <span className="muted small">{unitCost(p, l.unit)}</span>
                              {best ? <span className="sr-only"> {t('rfq.best')}</span> : null}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className="left">{t('inv.total')}</th>
                  <th />
                  {d.suppliers.map((s) => (
                    <th key={s.id}>
                      {s.state === 'replied' ? (
                        <span className="rfq-sup">
                          {money(s.total)}
                          {!s.complete ? (
                            <span className="muted small">{t('rfq.partial', { n: s.offered, of: d.lines.length })}</span>
                          ) : d.cheapest === s.supplier ? (
                            <span className="small rfq-cheapest">{t('rfq.cheapest')}</span>
                          ) : null}
                        </span>
                      ) : (
                        '–'
                      )}
                    </th>
                  ))}
                </tr>
                {manage && r.state === 'sent' ? (
                  <tr className="rfq-actions">
                    <th />
                    <th />
                    {d.suppliers.map((s) => (
                      <th key={s.id}>
                        <span className="rfq-sup">
                          <Button size="sm" icon="edit" onClick={() => setAnswer(s)} aria-label={t('rfq.enter_for', { name: s.name })}>
                            {s.state === 'invited' ? t('rfq.enter') : t('rfq.change')}
                          </Button>
                          {s.state === 'replied' && s.complete ? (
                            <Button size="sm" variant="ghost" onClick={() => award('supplier', s.supplier)} disabled={busy} aria-label={t('rfq.award_to', { name: s.name })}>
                              {t('rfq.award_all')}
                            </Button>
                          ) : null}
                        </span>
                      </th>
                    ))}
                  </tr>
                ) : null}
              </tfoot>
            </table>
          </div>
          {d.suppliers.some((s) => s.order) ? <p className="muted small">{t('rfq.orders_hint')}</p> : null}
        </div>
      )}
      {answer && d ? <AnswerDialog d={d} s={answer} onClose={() => setAnswer(null)} onDone={() => (setAnswer(null), refresh())} /> : null}
    </Dialog>
  );
}

function AnswerDialog({ d, s, onClose, onDone }: { d: RfqDetail; s: RfqDetail['suppliers'][number]; onClose: () => void; onDone: () => void }) {
  const { busy, run } = useSave();
  const [prices, setPrices] = useState<Record<string, string>>(Object.fromEntries(d.lines.map((l) => [l.item, s.prices[l.item] !== undefined ? costText(s.prices[l.item]!) : ''])));
  const [lead, setLead] = useState(s.lead_days !== null ? String(s.lead_days) : '');
  const [note, setNote] = useState(s.note ?? '');
  const parsed = Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, v.trim() ? parseCost(v) : undefined]));
  const bad = Object.values(parsed).some((v) => v === null);
  const any = Object.values(parsed).some((v) => typeof v === 'number');
  const l = lead.trim() ? (/^\d{1,3}$/.test(lead.trim()) ? Number(lead) : NaN) : null;
  const save = (declined: boolean) =>
    void run(
      async () => {
        await action('purchase.rfq_reply', {
          rfq: d.rfq.id,
          supplier: s.supplier,
          declined,
          prices: declined ? {} : Object.fromEntries(Object.entries(parsed).filter(([, v]) => typeof v === 'number')),
          lead_days: l,
          note: note.trim() || null,
        });
        onDone();
      },
      declined ? t('rfq.declined_ok', { name: s.name }) : t('rfq.answer_ok', { name: s.name }),
    );
  return (
    <Dialog
      title={t('rfq.answer_title', { name: s.name })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={() => save(true)} disabled={busy}>
            {t('rfq.declined')}
          </Button>
          <Button variant="primary" icon="check" onClick={() => save(false)} busy={busy} disabled={bad || !any || Number.isNaN(l)}>
            {t('rfq.save_answer')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('rfq.answer_hint')}</p>
        {d.lines.map((x) => (
          <Field key={x.item} label={`${x.name} · ${withUnit(x.qty, x.unit)}`} hint={t('sp.price_per', { unit: unitLabel(x.unit) })} error={parsed[x.item] === null ? t('sp.price_rule') : null}>
            <input className="input tabular" inputMode="decimal" value={prices[x.item] ?? ''} onChange={(e) => setPrices({ ...prices, [x.item]: e.target.value })} />
          </Field>
        ))}
        <div className="form-grid">
          <Field label={t('sp.lead_days')} error={Number.isNaN(l) ? t('sp.lead_rule') : null}>
            <input className="input" inputMode="numeric" maxLength={3} value={lead} onChange={(e) => setLead(e.target.value)} />
          </Field>
          <Field label={t('buy.note')}>
            <input className="input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
